import React, { useCallback, useEffect, useState } from 'react';

import DoubleBarChart from '@components/design/migrated/DoubleBarChart/DoubleBarChart';
import { useDoubleBarChart } from '@components/design/migrated/DoubleBarChart/hooks';
import { ColumnConfig, SortingOptionsType } from '@components/design/migrated/Table';

import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import usePersistedSorting from '@utils/hook/sorting/UsePersistedSorting';

import { getPerformanceAnalysisboardColumns } from '@components/performance-analysis-board/performance-analysis-table-columns/PerformanceAnalysisTableColumns';
import {
  ColumnHeaderForPA,
  DefaultSortingOptionsForPerformanceAnalysisboard,
  getDisplayNameFromHeaderKey,
  PerformanceAnalysisboardPg,
  PerformanceAnalysisboardTableId,
} from '@models/PerformanceAnalysisboard';
import { getChartOptions } from './ChartConfig';

import { ChartPickers } from '@components/chartpickers/ChartPickers';
import { FeatureSelectorType } from '@components/feature-selector/Types';
import { useInViewTrackingForPerformanceAnalysis } from '@components/performance-analysis-card/PerformanceAnalysisCardHooks';
import { PerformanceAnalysisView, PerformanceAnalysisViewMapping } from '@components/performance-analysis-card/Types';
import { NoResult } from '@pages/NoResult';
import { PERFORMANCE_ANALYSIS_CARD_TITLE } from '@pages/Utilities';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { FactRevenue, ShareType } from '@utils/pageConfig/Types';
import { renderVisualization } from '../utilities/ChartUtils';
import { modifiedPATableDoubleBarChartGrandTotalToTotalValue } from '@utils/TotalUtils';
import ErrorResultMessage from '@pages/ErrorResultMessage';

interface Props {
  performanceAnalysisboardPg: PerformanceAnalysisboardPg[];
  growthType: GrowthTypes;
  isLoading: boolean;
  featureSelectorType: FeatureSelectorType;
  factRevenue: FactRevenue;
  featureHandler: () => void;
  onKpiSelectHandler?: () => void;
  noResultHandler: () => void;
  volume: string;
  noPeriodExistForMulticountry?: boolean;
  isApiError?: boolean;
}

export const PerformanceAnalysisDoubleBarChart: React.FC<Props> = ({
  performanceAnalysisboardPg,
  growthType,
  isLoading,
  featureSelectorType,
  factRevenue,
  featureHandler,
  onKpiSelectHandler,
  noResultHandler,
  volume,
  noPeriodExistForMulticountry,
  isApiError,
}): JSX.Element => {
  const [columns, setColumns] = useState<ColumnConfig[]>([]);

  const modifiedPerformanceAnalysisPg = modifiedPATableDoubleBarChartGrandTotalToTotalValue(performanceAnalysisboardPg);

  const { sorting, updateOptions } = usePersistedSorting({
    tableId: PerformanceAnalysisboardTableId,
    defaultSorting: DefaultSortingOptionsForPerformanceAnalysisboard,
  });

  // Double bar chart will not have Revenue and Volume Share (Market vs Retailer) options
  const invalidKpiHeaderKeys: string[] = [
    ColumnHeaderForPA.marketRetailerRevenueShare,
    ColumnHeaderForPA.marketRetailerVolumeShare,
  ];

  useCallback(
    (optionsType: SortingOptionsType) => {
      updateOptions(optionsType);
    },
    [updateOptions],
  );

  useEffect(() => {
    if (performanceAnalysisboardPg) {
      const cols: ColumnConfig[] = getPerformanceAnalysisboardColumns(ShareType.MarketShare, volume);
      const validCols = cols.filter((col) => !invalidKpiHeaderKeys.includes(col.headerKey));
      setColumns(validCols);
    }
  }, [performanceAnalysisboardPg, sorting]);

  useEffect(() => {
    if (performanceAnalysisboardPg.length === 0) {
      noResultHandler();
    }
  }, [performanceAnalysisboardPg]);

  const headerKey = sorting.headerKey;

  const { ref } = useInViewTrackingForPerformanceAnalysis(
    PerformanceAnalysisView.BarChart,
    getDisplayNameFromHeaderKey(headerKey, volume),
    modifiedPerformanceAnalysisPg.length > 0,
    `${PERFORMANCE_ANALYSIS_CARD_TITLE} ${PerformanceAnalysisViewMapping[PerformanceAnalysisView.BarChart]}`,
    sorting,
  );

  const chartOptions = getChartOptions({
    headerKey,
    currencySymbol: factRevenue.currencySymbol,
    performanceAnalysisboardPg: modifiedPerformanceAnalysisPg,
    kpisConfig: columns,
    growthType,
    factRevenue,
  });

  const { options, handleChartCallback } = useDoubleBarChart(chartOptions);

  const renderDoubleBarChart = renderVisualization({
    isLoading: isLoading,
    fetchedApiData: modifiedPerformanceAnalysisPg,
    defaultData: [],
    jsxToRender: (
      <div ref={ref}>
        <DoubleBarChart options={options} callback={handleChartCallback} />
      </div>
    ),
    noResultJsx: <div ref={ref}>{NoResult()}</div>,
    errorMessageJsx: <div ref={ref}>{ErrorResultMessage()}</div>,
    noPeriodExistForMulticountry: noPeriodExistForMulticountry,
    isError: isApiError,
  });

  return (
    <>
      <ChartPickers
        sorting={sorting}
        updateSorting={updateOptions}
        kpisConfig={columns}
        getDisplayName={getDisplayNameFromHeaderKey}
        featureSelectorType={featureSelectorType}
        featureHandler={featureHandler}
        onKpiSelectHandler={onKpiSelectHandler}
        chartName={`${PERFORMANCE_ANALYSIS_CARD_TITLE} ${ElementType.BAR_CHART}`}
        factRevenue={factRevenue}
        volume={volume}
        isLoading={isLoading}
      />
      {renderDoubleBarChart}
    </>
  );
};
